import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { useCreateProduct, useStarlinkProducts } from "@/domains/products/services/products";
import { Product } from "@/shared/types/productTypes";
import { CreateProductRequest } from "@/shared/types/productTypes";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";

interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  product?: Product;
  selectedRegionCode: string;
}

export default function CreateProductDialog({
  open,
  onOpenChange,
  onSuccess,
  product,
  selectedRegionCode,
}: CreateProductDialogProps) {
  const { data: starlinkProducts } = useStarlinkProducts(open, selectedRegionCode);
  const createProduct = useCreateProduct(selectedRegionCode);
  const [formData, setFormData] = useState<CreateProductRequest | null>(null);

  useEffect(() => {
    if (!open) {
      setFormData(null);
    }
  }, [open]);

  useEffect(() => {
    if (product) {
      setFormData(product);
    } else {
      setFormData({
        productReferenceId: '',
        orbiusProductName: '',
        starlinkProductName: '',
        maxNumberOfUserTerminals: 0,
        dataProduct: {
          dataAmount: 0,
        },
        l2vpnSupported: false,
      });
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate Starlink Subscription
    if (!formData?.productReferenceId) {
      showError(ERROR.SUBSCRIPTION.SELECT_STARLINK_SUBSCRIPTION);
      return;
    }


    try {
      await createProduct.mutateAsync({
        ...formData,
        dataProduct: {
          ...formData?.dataProduct,
          dataAmount: Number.parseFloat(formData?.dataProduct?.dataAmount.toString()),
        },
      });
      showSuccess(SUCCESS.SUBSCRIPTION.CREATED);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SUBSCRIPTION.CREATE);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('dataProduct.')) {
      const dataProductField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        dataProduct: {
          ...prev.dataProduct,
          [dataProductField]: dataProductField === 'dataAmount' ? Number.parseFloat(value) : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="create-product-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Subscription</DialogTitle>
          <DialogDescription>Create a new subscription for a Starlink product.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Select
                value={formData?.productReferenceId || ''}
                onValueChange={(id) => {
                  const product = starlinkProducts?.find(product => product.productReferenceId === id);
                  setFormData(prev => ({
                    ...prev,
                    productReferenceId: id,
                    starlinkProductName: product?.name,
                    maxNumberOfUserTerminals: product?.maxNumberOfUserTerminals,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Starlink Subscription" />
                </SelectTrigger>
                <SelectContent>
                  {starlinkProducts?.map((s) => (
                    <SelectItem key={s.productReferenceId} value={s.productReferenceId}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="orbiusProductName">Subscription Name</Label>
                <Input
                  id="orbiusProductName"
                  name="orbiusProductName"
                  value={formData?.orbiusProductName}
                  onChange={handleInputChange}
                  placeholder="Enter Subscription Name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataProduct.dataAmount">Base Data Consumption (GB)</Label>
                <Input
                  id="dataProduct.dataAmount"
                  name="dataProduct.dataAmount"
                  type="number"
                  value={formData?.dataProduct?.dataAmount}
                  onChange={handleInputChange}
                  placeholder="Enter Base Data Consumption"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between space-x-2">
                <Label htmlFor="l2vpnSupported">L2VPN Supported</Label>
                <Switch
                  id="l2vpnSupported"
                  checked={formData?.l2vpnSupported ?? false}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    l2vpnSupported: checked ?? false,
                  }))}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
              <p className="text-sm italic red text-red-500">
                *When enabled, this product will support L2VPN configuration.
              </p>
              <p className="text-sm italic red text-red-500">
                *When disabled, this product will support public-ip configuration.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={createProduct.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createProduct.isPending}
              className="gap-2">
              {createProduct.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Subscription'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}